import { Block } from '@aloft/toolkit-sui';

import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { Tkv2ReferenceViewDrawer } from '../../modules/referenceView/Tkv2ReferenceViewDrawer.tsx';
import { CompSelectionProperty } from './types.ts';

type RecCompSelectionContextValue = {
  openSimilarityScoreWeightingModal: () => void;
  openColumnSettings: () => void;
  openQualityTierBreakdown: () => void;
  openFilterDrawer: () => void;
  applySearchFilters: () => void;
  isFilterPanelOpen?: boolean;
  referenceDrawerContent: ReactNode | null;
  setReferenceDrawerContent: (content: ReactNode | null) => void;
  visibleMapProperties: CompSelectionProperty[] | undefined;
  setVisibleMapProperties: (properties: CompSelectionProperty[] | undefined) => void;
};

type RecCompSelectionProviderProps = PropsWithChildren<{
  value: Omit<
    RecCompSelectionContextValue,
    | 'referenceDrawerContent'
    | 'setReferenceDrawerContent'
    | 'visibleMapProperties'
    | 'setVisibleMapProperties'
  >;
}>;

const RecCompSelectionContext = createContext<RecCompSelectionContextValue | null>(null);

export const RecCompSelectionProvider = ({ children, value }: RecCompSelectionProviderProps) => {
  const [referenceDrawerContent, setReferenceDrawerContentState] = useState<ReactNode | null>(null);
  const [visibleMapProperties, setVisibleMapPropertiesState] = useState<
    CompSelectionProperty[] | undefined
  >(undefined);
  const setReferenceDrawerContent = useCallback((content: ReactNode | null) => {
    setReferenceDrawerContentState(content);
  }, []);
  const setVisibleMapProperties = useCallback((properties: CompSelectionProperty[] | undefined) => {
    setVisibleMapPropertiesState((current) => (current === properties ? current : properties));
  }, []);

  const contextValue = useMemo(
    () => ({
      ...value,
      referenceDrawerContent,
      setReferenceDrawerContent,
      visibleMapProperties,
      setVisibleMapProperties,
    }),
    [
      value,
      referenceDrawerContent,
      setReferenceDrawerContent,
      visibleMapProperties,
      setVisibleMapProperties,
    ],
  );

  return (
    <RecCompSelectionContext.Provider value={contextValue}>
      {children}
    </RecCompSelectionContext.Provider>
  );
};

export const useRecCompSelectionContext = (): Partial<RecCompSelectionContextValue> =>
  useContext(RecCompSelectionContext) ?? {};

export const RecCompSelectionReferenceViewDrawer = ({ children }: PropsWithChildren) => {
  const { referenceDrawerContent } = useRecCompSelectionContext();

  return (
    <Tkv2ReferenceViewDrawer
      drawerContent={
        <Block height="100%" width="100%" position="relative" overflow="hidden">
          {referenceDrawerContent}
        </Block>
      }
      canClose={false}
    >
      {children}
    </Tkv2ReferenceViewDrawer>
  );
};
